import type { ReactElement, ReactNode } from 'react';
import { toast as sonnerToast } from 'sonner';
import { Button } from './Button.js';
import { cn } from './cn.js';
import { Icon, type IconName } from './Icon.js';
import { Toaster as ToasterPrimitive } from './primitives/sonner.js';

/**
 * O popup transitório — um caso que `Modal` não cobre.
 *
 * `DESIGN.md` chama diálogo modal de "quase sempre preguiça" porque ele
 * INTERROMPE: a tela atrás trava até alguém decidir. Um aviso que ninguém
 * pediu — "chegou uma demanda nova" — é o oposto: aparece sozinho, some
 * sozinho, e nunca impede o que a pessoa já estava fazendo. As duas mecânicas
 * não competem, e por isso `Toast` não é uma variação de `Modal`.
 *
 * `<Toaster />` monta uma vez, no topo da árvore (`Layout.tsx`); qualquer
 * tela chama `showToast(...)` e o popup aparece no canto certo sozinho.
 */
export function Toaster() {
  return (
    <ToasterPrimitive
      position="top-right"
      gap={8}
      toastOptions={{ unstyled: true, className: 'flex w-full justify-end' }}
    />
  );
}

/**
 * O conteúdo do popup — ícone, título e o resto, na mesma anatomia do `Alert`.
 *
 * Sem `tone`: hoje só existe o caso informativo ("aconteceu algo em outro
 * lugar"), e um popup de erro sozinho no canto da tela — sem contexto do que
 * falhou, escondido atrás de qualquer outra janela — não é onde erro se
 * mostra. Quando um segundo caso aparecer, o tom entra do mesmo jeito que
 * entrou em `Alert`.
 */
export function Toast({
  title,
  children,
  icon = 'informacao',
  onClose,
}: {
  title: string;
  children?: ReactNode;
  icon?: IconName;
  /** Sem isto, o popup só some sozinho — quem não quis esperar não tem o quê fazer. */
  onClose?: () => void;
}) {
  return (
    <div
      role="status"
      className={cn(
        'flex w-full max-w-sm gap-2.5 rounded-grande border border-borda',
        'bg-superficie px-3.5 py-3 text-sm shadow-cartao',
      )}
    >
      <Icon nome={icon} className="mt-px shrink-0 text-primaria-texto" />
      <div className="min-w-0 flex-1 text-texto">
        <p className="font-semibold">{title}</p>
        {children}
      </div>
      {onClose && (
        <Button
          variant="discreto"
          size="sm"
          icon="fechar"
          aria-label="Fechar"
          className="-mr-1 -mt-1 shrink-0"
          onClick={onClose}
        />
      )}
    </div>
  );
}

/**
 * Mostra o popup por `duration` ms — mas quem não quer esperar fecha na hora.
 *
 * `render` recebe `dismiss`, não o `id` cru do `sonner`: a biblioteca fica
 * inteira dentro deste arquivo, e quem chama `showToast` nunca precisa saber
 * que existe um `toast.dismiss(id)` por trás.
 *
 * `toast.custom` e não `toast(mensagem)`: o conteúdo é sempre um `<Toast>`
 * nosso, nunca o texto simples que o `sonner` desenharia com a própria cara.
 */
export function showToast(
  render: (dismiss: () => void) => ReactElement,
  duration = 8_000,
): void {
  sonnerToast.custom((id) => render(() => sonnerToast.dismiss(id)), { duration });
}
